import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Col, Container, Row } from '../../../Shared/Grid'
import dayjs from 'dayjs'
import { Trans, useTranslation, withTranslation } from 'react-i18next'
import './index.scss'
import Icon from '../../../Shared/Icon'
import {
  addDistributionChannelFavourite,
  getDistributionChannels,
  removeDistributionChannelFavourite
} from '../../../../api'
import Loader from '../../../Shared/Loader'
import activityTrackingService from '../../../../services/activityTrackingService'
import PageHead from '../../../Shared/PageHead'
import SearchField from '../../../Shared/SearchField'
import { withRouter } from '../../../Shared/Routing/withRouter'
import PropTypes from 'prop-types'
import PublishInfo from './publishInfo'
import { dateFormat, timeFormat, updateCycleTooltipMap } from './constants'
import ChannelFilters from './ChannelFilters'
import Toggle from '../../../Shared/Toggle'
import TooltipWrapper from '../../../Shared/TooltipWrapper'
import ImageConsumptionBadges from './ImageConsumptionBadges'

const updateFreqPriority = {
  'On request': 1,
  'Quarterly': 2,
  'Bi-monthly': 3,
  'Monthly': 4,
  'Bi-Weekly': 5,
  'Weekly': 6,
  'Daily': 7,
  'Twice daily': 8,
  'Every 6 hours': 9,
  'Real time': 10,
}

const statusPriority = {
  'Coming soon': 1,
  'Planned': 2,
  'Live': 3,
}

const SortableHeader = memo(({ title, onSort, field, currentField, currentSortDir }) => {
  const className = `icon${currentField === field ? ` ${currentSortDir}` : ''}`
  return (
    <th className="sortable" onClick={() => onSort(field)}>
      {title}
      {<span className={className}/>}
    </th>
  )
})

const ContentMonitor = (props) => {
  const giataId = parseInt(props.router.params.id, 10)
  const [selectedDistributionTypes, setSelectedDistributionTypes] = useState([])
  const [selectedContentUsed, setSelectedContentUsed] = useState([])
  const [channels, setChannels] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredChannels, setFilteredChannels] = useState([])
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false)
  const channelListScrollableDiv = useRef()
  const [sortDir, setSortDir] = useState('desc')
  const [sortField, setSortField] = useState('contentSent')
  const { t } = useTranslation()
  useEffect(() => {
    activityTrackingService.trackPageView('Content Monitor', {}, false)
    getDistributionChannels(giataId).then((data) => {
      setLoading(false)
      setChannels((data.records ?? []).map(record => ({
        ...record,
        lastProcessedAt: record.lastProcessedAt ? dayjs(record.lastProcessedAt) : null
      })))
    }).catch(() => {
      setLoading(false)
    })

  }, [giataId])
  useEffect(() => {
    setFilteredChannels(
      channels.filter((channel) => {
        return (
          selectedDistributionTypes.length === 0
          || channel.types.some(type => selectedDistributionTypes.includes(type))
        ) && (
          selectedContentUsed.length === 0
          || (channel.contentUsed ?? []).some(type => selectedContentUsed.includes(type))
        ) && (
          !searchTerm
          || channel.name.toLowerCase().includes(searchTerm.toLowerCase())
        ) && (
          !showFavouritesOnly
          || (channel.isFavourite && channel.status === 'Live')
        )
      }).sort((a, b) => {
        let fieldA
        let fieldB
        switch (sortField) {
          case 'name':
            fieldA = a[sortField].toLowerCase()
            fieldB = b[sortField].toLowerCase()
            break
          case 'contentUsed':
            fieldA = a[sortField][0] ?? ''
            fieldB = b[sortField][0] ?? ''
            break
          case 'contentSent':
            fieldA = a['lastProcessedAt'] ? a['lastProcessedAt'] : (updateFreqPriority[a['updateCycle']] ?? 0)
            fieldB = b['lastProcessedAt'] ? b['lastProcessedAt'] : (updateFreqPriority[b['updateCycle']] ?? 0)
            break
          case 'status':
            fieldA = statusPriority[a[sortField]] ?? 0
            fieldB = statusPriority[b[sortField]] ?? 0
            break
          default:
            return 0
        }

        // pushing empty values to bottom first
        // then performing actual compare
        if (!fieldA) {
          return !fieldB ? 0 : 1
        } else if (!fieldB) {
          return -1
        }

        const comparison = fieldA < fieldB ? -1 : fieldA > fieldB ? 1 : 0
        if (sortField !== 'contentSent') {
          // no need to deal with any date shenanigans, so we simply return the comparison
          return sortDir === 'asc' ? comparison : -comparison
        }

        // we need to consider date fields separately
        // other values are pushed to the bottom and sorted as usual after
        if (!isDate(fieldA) && isDate(fieldB)) return 1
        if (isDate(fieldA) && !isDate(fieldB)) return -1
        if (!isDate(fieldA) && !isDate(fieldB)) return sortDir === 'asc' ? comparison : -comparison

        // sort by the date fields but we can still use the comparison since the dayjs objects are comparable
        return sortDir === 'asc' ? comparison : -comparison
      })
    )
  }, [channels, selectedDistributionTypes, selectedContentUsed, searchTerm, sortField, sortDir, showFavouritesOnly])

  const triggerSort = useCallback((_sortField) => {
    if (_sortField === sortField) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    }
    setSortField(_sortField)
  }, [sortField, sortDir])

  const toggleFavourite = (channel) => {
    const updatedIsFavourite = !channel.isFavourite

    setChannels(prevChannels => prevChannels.map(ch =>
      ch.id === channel.id ? { ...ch, isFavourite: updatedIsFavourite } : ch
    ))

    const { fetchPromise } = updatedIsFavourite
      ? addDistributionChannelFavourite(giataId, channel.id)
      : removeDistributionChannelFavourite(giataId, channel.id)

    fetchPromise.catch(() => {
      setChannels(prevChannels => prevChannels.map(ch =>
        ch.id === channel.id ? { ...ch, isFavourite: !updatedIsFavourite } : ch
      ))
    })
  }

  const formatDateForChannel = (lastProcessedAt, today, yesterday) => {

    const isToday = lastProcessedAt.isSame(today, 'day')
    const isYesterday = lastProcessedAt.isSame(yesterday, 'day')

    if (isToday) {
      return t(
        'contentMonitor.channelList.body.contentSent.today',
        { 'time': lastProcessedAt.format(timeFormat) }
      )
    }
    if (isYesterday) {
      return t(
        'contentMonitor.channelList.body.contentSent.yesterday',
        { 'time': lastProcessedAt.format(timeFormat) }
      )
    }
    return `${lastProcessedAt.format(dateFormat + ' ' + timeFormat)}`
  }

  const isDate = (value) => {
    return value instanceof dayjs
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedDistributionTypes([])
    setSelectedContentUsed([])
  }

  const getUpdateCycleContent = (updateCycle) => {
    const tooltipKey = updateCycleTooltipMap[updateCycle]
    const textContent = <div className="truncated-text">{updateCycle}</div>

    if (!tooltipKey) return textContent

    return (
      <div className="update-cycle-trigger">
        {textContent}
        <TooltipWrapper
          content={<Trans i18nKey={tooltipKey}/>}
          place="bottom-end"
          maxWidth="320px"
          bgColor="#FEF4C9"
          positionStrategy="fixed"
        >
          <Icon
            extraClassNames="update-cycle-trigger__icon"
            type="how"
            size="extra-small"
          />
        </TooltipWrapper>
      </div>
    )
  }

  const createChannelList = (channels) => {
    const today = dayjs()
    const yesterday = dayjs().subtract(1, 'day')

    if (channels.length === 0) {
      const hasActiveFilters = [searchTerm, selectedDistributionTypes, selectedContentUsed].some(f => f.length > 0)
      const isNoFavourites = showFavouritesOnly && !hasActiveFilters

      return (
        <tr>
          <td colSpan={5} className="lighter-text empty-state-text">
            {t(`contentMonitor.channelList.body.${isNoFavourites ? 'noFavouritesFound' : 'noChannelsFound'}`)}
            {!isNoFavourites && (
              <button className="clear-filters-btn" onClick={clearFilters}>
                Clear all
              </button>
            )}
          </td>
        </tr>
      )
    }

    return channels.map((channel) => {
      const isLive = channel.status === 'Live'

      return (
        <tr
          key={channel.name + channel.types.join()}
          className={isLive ? undefined : 'channel-row--inactive'}
        >
          <td className="channel-icon-column">
            {isLive
              ? <Icon
                size={'extra-small'}
                type={channel.isFavourite ? 'bookmark-filled' : 'bookmark'}
                background={'transparent'}
                onClick={() => toggleFavourite(channel)}
              />
              : getStatusIcon(channel)
            }
          </td>
          <td data-title={t('contentMonitor.channelList.tableHeader.channelName')}>
            <div className="channel-name-container">
              <div className="truncated-text">
                <a href={channel.link} target={'_blank'} rel={'noreferrer'} className="channel-name-link">
                  <span className="channel-name-link__name">{channel.name}</span>
                </a>
              </div>
            </div>
          </td>
          <td data-title={t('contentMonitor.channelList.tableHeader.contentUsed')}>
            <div className="truncated-text">
              {getContentUsageContent(channel)}
            </div>
          </td>
          <td data-title={t('contentMonitor.channelList.tableHeader.imageConsumption')}>
            <ImageConsumptionBadges imageConsumptionLabels={channel.imageConsumptionLabels ?? []}/>
          </td>
          <td data-title={t('contentMonitor.channelList.tableHeader.contentSent')}>
            {getContentSentContent(channel, today, yesterday)}
          </td>
        </tr>
      )
    })
  }

  const getContentUsageContent = (channel) => {
    if (channel.status === 'Planned') {
      return t('contentMonitor.channelList.body.contentSent.evaluationInProgress')
    }
    if (channel.status === 'Coming soon') {
      return t('contentMonitor.channelList.body.contentSent.integrationInProgress')
    }
    if ((channel.contentUsed ?? []).length > 0) {
      return channel.contentUsed.join(', ')
    }
    return <span className="lighter-text">{t('contentMonitor.channelList.body.noInfo')}</span>
  }

  const getContentSentContent = (channel, today, yesterday) => {
    if (channel.status === 'Planned') {
      return <div className="truncated-text">
        {t('contentMonitor.channelList.body.contentSent.evaluationInProgress')}
      </div>
    }
    if (channel.status === 'Coming soon') {
      return <div className="truncated-text">
        {t('contentMonitor.channelList.body.contentSent.integrationInProgress')}
      </div>
    }
    if (channel.lastProcessedAt) {
      return <div className="truncated-text">
        {formatDateForChannel(channel.lastProcessedAt, today, yesterday)}
      </div>
    }
    return getUpdateCycleContent(channel.updateCycle)
  }

  const getStatusIcon = (channel) => {
    if (channel.status === 'Planned') {
      return <Icon
        size={'extra-small'}
        background={'transparent'}
        type={'calendar'}
        tooltip={<Trans i18nKey={'contentMonitor.channelList.body.status.planned'}/>}
        useNewTooltip={true}
        tooltipPosition="left"
        tooltipProps={{ positionStrategy: 'fixed' }}
      />
    }
    if (channel.status === 'Coming soon') {
      return <Icon
        size={'extra-small'}
        background={'transparent'}
        type={'clock'}
        tooltip={<Trans i18nKey={'contentMonitor.channelList.body.status.comingSoon'}/>}
        useNewTooltip={true}
        tooltipPosition="left"
        tooltipProps={{ positionStrategy: 'fixed' }}
      />
    }
  }

  const channelTableHeaders = [
    {
      field: 'name',
      title: t('contentMonitor.channelList.tableHeader.channelName')
    },
    {
      field: 'contentUsed',
      title: t('contentMonitor.channelList.tableHeader.contentUsed')
    }
  ]

  const contentSentTableHeader = {
    field: 'contentSent',
    title: t('contentMonitor.channelList.tableHeader.contentSent')
  }

  return <div className="page page-content-monitor">
    <Container fluid gutterWidth={5}>
      <Row>
        <Col xs={12}>
          <PageHead
            title={t('contentMonitor.title')}
            subtitle={t('contentMonitor.subtitle')}
          />
        </Col>
        <Col xs={12}>
          <br/>
          <h3 className="your-giata-drive-header">{t('contentMonitor.publishInfo.header')}</h3>
        </Col>
      </Row>
      <PublishInfo
        giataId={giataId}
        confirmedImagesCount={props.confirmedImagesCount}
        confirmedPropertyImagesCount={props.confirmedPropertyImagesCount}
        confirmedRoomImagesCount={props.confirmedRoomImagesCount}
        roomsCount={props.roomsCount}
        factsCount={props.factsCount}
        lastImageUploadDate={props.lastImageUploadDate}
      />
    </Container>
    <br/>
    <Container fluid gutterWidth={5}>
      {loading && <Loader/>}
      <div className="distribution-channels-header">
        <h2 className="distribution-channels-header__title">{t('contentMonitor.channelList.header.title')}</h2>
        <div className="distribution-channels-header__toggle-container">
          <Toggle
            checked={showFavouritesOnly}
            onChange={setShowFavouritesOnly}
            labelText={t('contentMonitor.channelList.header.showFavourites')}
            inputId="show-favourites-toggle"
          />
        </div>
      </div>
      <div className="distribution-channels-container">
        <ChannelFilters
          channels={channels}
          onFilterTypes={setSelectedDistributionTypes}
          onFilterContentUsed={setSelectedContentUsed}
          selectedContentUsed={selectedContentUsed}
          selectedTypes={selectedDistributionTypes}
        />
        <div className="distribution-channels-table-container">
          <div className="distribution-channels-table-header-container">
            <div>
              <h3 className="distribution-channels-table-header">
                Content consumption
              </h3>
              <span className="distribution-channels-table-subheader">
              These channels have access to your GIATA DRIVE content.
            </span>
            </div>
            <div className="distribution-channels-table-search-container">
              <SearchField
                placeholderText={t('SEARCH_CHANNEL')}
                onInput={(e) => setSearchTerm(e.target.value)}
                onClear={() => setSearchTerm('')}
                value={searchTerm}
              />
            </div>
          </div>
          <div
            ref={channelListScrollableDiv}
            style={{ position: 'relative', maxHeight: '600px', overflowY: 'scroll' }}
            className="table-responsive-vertical distribution-channels-table"
          >
            <table className="table-hover">
              <thead>
              <tr>
                <th className="channel-icon-header"></th>
                {channelTableHeaders.map(header => (
                  <SortableHeader
                    key={header.field}
                    title={header.title}
                    onSort={triggerSort}
                    currentField={sortField}
                    currentSortDir={sortDir}
                    field={header.field}
                  />
                ))}
                <th className="sortable">{t('contentMonitor.channelList.tableHeader.imageConsumption')}</th>
                <SortableHeader
                  key={contentSentTableHeader.field}
                  title={contentSentTableHeader.title}
                  onSort={triggerSort}
                  currentField={sortField}
                  currentSortDir={sortDir}
                  field={contentSentTableHeader.field}
                />
              </tr>
              </thead>
              <tbody>
              {!loading && createChannelList(filteredChannels)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Container>
  </div>
}

ContentMonitor.propTypes = {
  confirmedImagesCount: PropTypes.number,
  confirmedRoomImagesCount: PropTypes.number,
  confirmedPropertyImagesCount: PropTypes.number,
  factsCount: PropTypes.number,
  roomsCount: PropTypes.number,
  lastImageUploadDate: PropTypes.instanceOf(Date),
}

ContentMonitor.defaultProps = {
  confirmedImagesCount: 0,
  confirmedRoomImagesCount: 0,
  confirmedPropertyImagesCount: 0,
  factsCount: 0,
  roomsCount: 0,
  lastImageUploadDate: null,
}

export default withTranslation()(withRouter(ContentMonitor))
